import React from 'react'

import Popup from 'shared/components/Popup'
import Table from 'shared/components/Table'
import UserTableRowOptions from 'components/UserTableRowOptions/container'
import lodash from 'lodash'
import usePermission from 'hooks/usePermission'
import constants from 'config/constants'

/**
 * @typedef User
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {'Owner'|'Admin'|'Collaborator'|'Billing'} role
 * @property {boolean} disable_sso
 * @property {'active'|'invited'} status
 * @property {boolean} is_2fa_enabled
 * @property {string[]} team_member_mapping_emails
 */

/**
 * @param {object} props
 * @param {User[]} props.dataSource
 * @param {React.FunctionComponent=} props.UserOptionsComponent
 * @param {object[]} props.roleOptions
 * @param {object[]} props.surveyAccessSelectableRoles
 * @param {object[]} props.dashbardAccessesSelectableRoles
 * @param {object} props.userMe
 * @param {Array<{email: string, name: string}>} [props.teamMembers]
 * @param {boolean} [props.isTeamMembersLoading]
 */
export default function UsersTable({
  dataSource,
  UserOptionsComponent = UserTableRowOptions,
  roleOptions,
  userMe,
  surveyAccessSelectableRoles,
  dashbardAccessesSelectableRoles,
  teamMembers,
  isTeamMembersLoading,
}) {
  const workosConnectionStatus = userMe.workos_sso_connection_status

  const { hasPermission } = usePermission()

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 200,
      render(_, user) {
        const label = user.name || user.email
        const description = user.name ? user.email : null
        return (
          <div className="flex items-center">
            <Popup
              basic
              tooltip={
                <div>
                  <div>{label}</div>
                  {description && <div>{user.email} </div>}
                </div>
              }
              size="mini"
              inverted
            >
              <div className="truncate leading-normal">{label}</div>
            </Popup>
          </div>
        )
      },
      sorter(userA, userB) {
        const labelA = userA.name || userA.email
        const labelB = userB.name || userB.email
        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      ellipsis: true,
      sorter: (a, b) => {
        const sortOrderList = ['owner', 'admin', 'manager', 'collaborator', 'billing']

        const getIndex = (role) =>
          sortOrderList.indexOf(role) !== -1 ? sortOrderList.indexOf(role) : sortOrderList.length

        const indexA = getIndex(a.role)
        const indexB = getIndex(b.role)

        // Sort by predefined order if both are in the list
        if (indexA !== sortOrderList.length && indexB !== sortOrderList.length) {
          return indexA - indexB
        }

        // Sort alphabetically if both are not in the predefined order
        if (indexA === sortOrderList.length && indexB === sortOrderList.length) {
          return a.role.localeCompare(b.role)
        }

        // Prioritize predefined roles over non-listed ones
        return indexA - indexB
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
      render(_, user) {
        return lodash.capitalize(user.role)
      },
    },
    {
      title: 'Team member mapping',
      dataIndex: 'team_member_mapping_emails',
      key: 'team_member_mapping_emails',
      width: 200,
      ellipsis: true,
      /**
       * @param {*} _
       * @param {User} user
       */
      render(_, user) {
        const teamMemberMappings = [user.email].concat(user.team_member_mapping_emails)
        return (
          <Popup
            basic
            tooltip={
              <ul className="m0 p0 list-none">
                {teamMemberMappings.map((team_member, index) => {
                  return <li key={index}>{team_member}</li>
                })}
              </ul>
            }
            size="mini"
            inverted
          >
            <div className="truncate leading-normal">{teamMemberMappings.join(', ')}</div>
          </Popup>
        )
      },
    },
    {
      title: 'Authentication',
      dataIndex: 'is_2fa_enabled',
      key: 'is_2fa_enabled',
      width: 150,
      render(is2faEnabled, user) {
        let authenticationLabel = ''
        let twoFaLabel = ''
        let customLoginLabel = ''

        if (workosConnectionStatus === 'active' && user.enforce_sso) {
          if (user.disable_sso) {
            authenticationLabel = <span className="ui basic orange label">SSO optional</span>
          } else {
            authenticationLabel = <span className="ui basic label">SSO enforced</span>
          }
        } else if (workosConnectionStatus === 'active' && !user.enforce_sso) {
          authenticationLabel = <span className="ui basic orange label">SSO optional</span>
        }

        if (user.is_custom_login) {
          customLoginLabel = <span className="ui basic orange label !ml-0">Custom methods</span>
        }

        if (is2faEnabled) {
          twoFaLabel = <span className="ui basic label !ml-0">2FA</span>
        }

        return (
          <div className="flex flex-wrap gap-1">
            {!authenticationLabel && !twoFaLabel && !customLoginLabel ? (
              '—'
            ) : (
              <React.Fragment>
                {authenticationLabel} {customLoginLabel} {twoFaLabel}
              </React.Fragment>
            )}
          </div>
        )
      },
      sorter(userA, userB) {
        if (userA.disable_sso !== userB.disable_sso) {
          return userA.disable_sso - userB.disable_sso
        }

        if (userA.is_2fa_enabled !== userB.is_2fa_enabled) {
          return userB.is_2fa_enabled - userA.is_2fa_enabled
        }

        return 0
      },

      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render(status) {
        if (status === 'active') {
          return <span className="ui basic green label">Active</span>
        }

        if (status === 'invited') {
          return <span className="ui basic orange label">Invited</span>
        }

        return 'n/a'
      },
      sorter(userA, userB) {
        return (userA.status || '').localeCompare(userB.status)
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
  ]

  if (hasPermission(constants.permissions.manageUsers)) {
    columns.push({
      title: '',
      key: 'action',
      align: 'right',
      fixed: 'right',
      width: 60,
      render(_, user) {
        return (
          <UserOptionsComponent
            user={user}
            roleOptions={roleOptions}
            surveyAccessSelectableRoles={surveyAccessSelectableRoles}
            dashbardAccessesSelectableRoles={dashbardAccessesSelectableRoles}
            teamMembers={teamMembers}
            isTeamMembersLoading={isTeamMembersLoading}
          />
        )
      },
    })
  }

  return (
    <Table
      variant="indexPage"
      clickable={false}
      pagination={false}
      dataSource={dataSource}
      columns={columns}
    />
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/UsersTable/index.js